import { generateTestId } from "@client-shared";
import { zoneFromPosition } from "@shared/lib/intensity/intensity-estimator";
import { isIntensityPosition, type IntensityPosition, type IntensityZone } from "@shared/types/intensity";

import type { ProductFormData } from "../schemas/product-schema";
import { DEFAULT_END_ELEVATION, DEFAULT_START_ELEVATION } from "../constants";
import { isProductLevelLocked as isProductLevelLockedFn } from "../utils";
import type { ProductFormProps } from "./product-form.types";

export const setProductFormTestId = (identifier: string): string => generateTestId("ProductForm", identifier);

export function mapProductToFormDefaults(product: ProductFormProps["product"]): Partial<ProductFormData> {
  // Ship-to is bag-size based. A variant keeps its own saved value;
  // otherwise it defaults to the product's ship-to — which, for the SS "New Bag
  // Size" flow, is the previously-published sibling bag size's Salesforce
  // ship-to (e.g. "NL" only). This is what makes a new bag size inherit the
  // sibling's ship-to instead of the account-level all-destinations default.
  // Home-country lock is (re)applied at the variant UI level.
  const mappedVariants = product.variants.map((variant) => ({
    ...variant,
    grind: variant.grind ?? [],
    price: variant.price ?? "",
    wholesalePrice: variant.wholesalePrice ?? "",
    term: variant.term ?? "Long",
    startDate: variant.startDate ?? "",
    ship_to: variant.ship_to ?? product.ship_to ?? "",
    isProductSelectedForImport: variant.isProductSelectedForImport ?? false,
  }));

  const mappedLocalVariants = product.localVariants?.map((variant) => ({
    ...variant,
    grind: variant.grind ?? [],
    price: variant.price ?? "",
    wholesalePrice: variant.wholesalePrice ?? "",
    term: variant.term ?? "Long",
    startDate: variant.startDate ?? "",
    ship_to: variant.ship_to ?? product.ship_to ?? "",
    isProductSelectedForImport: variant.isProductSelectedForImport ?? false,
  }));

  // The `imageUploads` form field is the user's *selection* (a validation gate),
  // not the available thumbnails — those live in `availableImages` state and are
  // what actually gets imported (see the onChange merge). For an editable
  // product it must start empty so auto-loaded Shopify images don't validate
  // green until the roaster actually picks one. Locked/imported
  // products can't pick (the upload is read-only), so their auto-loaded image
  // is kept as the selection.
  const imageUploads = isProductLevelLockedFn(product) ? (product.imageUploads ?? null) : null;

  return {
    title: product.title ?? "",
    description: product.description ?? "",
    roasterNotes: product.roasterNotes ?? "",
    imageUploads,
    mainFlavorCategory: product.mainFlavorCategory ?? "",
    secondaryFlavorCategory: product.secondaryFlavorCategory ?? "",
    isDecaf: product.isDecaf ?? false,
    productType: product.productType ?? "",
    roastLevel: product.roastLevel ?? "",
    process: product.process ?? "",
    varietal: product.varietal ?? "",
    tastingNotes: product.tastingNotes ?? [],
    brewMethod: product.brewMethod ?? [],
    certifications: product.certifications ?? null,
    badges: product.badges ?? null,
    character: product.character ?? "",
    region: product.region ?? "",
    country: product.country ?? "",
    elevationStart: product.elevationStart ?? DEFAULT_START_ELEVATION,
    elevationEnd: product.elevationEnd ?? DEFAULT_END_ELEVATION,
    // Intensity is optional, so these start empty rather than at a scale
    // default — but they must still be present, or RHF reports the fields as
    // uncontrolled the first time the roaster touches them.
    intensityPosition: product.intensityPosition ?? null,
    intensityZone: product.intensityZone ?? "",
    closestMatchCoffee: product.closestMatchCoffee ?? null,
    variants: mappedVariants as ProductFormData["variants"],
    uuid: product.uuid,
    localVariants: mappedLocalVariants as ProductFormData["localVariants"],
    localTitle: product.localTitle,
    isProductFormValid: product.isProductFormValid ?? false,
    hasDraft: product.hasDraft,
    hasVariantsDraft: product.hasVariantsDraft,
  };
}

/**
 * Every field is required-but-undefinable on purpose. Spreading an *optional*
 * property over another optional property widens to a union rather than
 * replacing it, so an optional shape here would leave `intensityPosition`
 * typed `IntensityPosition | null | undefined` at the merge site — the exact
 * thing this helper exists to prevent.
 *
 * The two fields normalise to an explicit `null` / `""` rather than
 * `undefined` when unset. This matters beyond the merge above: a saved draft
 * round-trips through a backend API call as JSON, which drops `undefined`
 * properties entirely but keeps an explicit `null`/`""` — so
 * `handle-display-draft.ts` can reliably tell "this draft went through the
 * current form" (the key is present, even when empty) apart from "this draft
 * predates the intensity feature" (the key is absent) by checking for the
 * key's presence, not its value.
 */
export interface NormalisedIntensityFields {
  intensityPosition: IntensityPosition | null;
  intensityZone: IntensityZone | "";
}

/** The subset of form values the normaliser reads. */
export interface IntensityFormValues {
  intensityPosition?: number | null;
  intensityZone?: string;
}

/**
 * Collapses the form's "unscored" representation (`null` / `""`) to a form
 * `FormProduct` can hold explicitly, and drops anything off-scale.
 *
 * The zone is always derived from the sanitized position rather than read
 * from `formData.intensityZone` — normalizing the two independently can
 * leave a stale zone paired with a cleared/invalid position.
 */
export const normaliseIntensityFields = (formData: IntensityFormValues): NormalisedIntensityFields => {
  const intensityPosition = isIntensityPosition(formData.intensityPosition) ? formData.intensityPosition : null;
  return {
    intensityPosition,
    intensityZone: intensityPosition === null ? "" : zoneFromPosition(intensityPosition),
  };
};
